/* eslint-disable no-nested-ternary */
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Ordinal from './Ordinal';
import Timer from './Timer';

import styles from '../../../styles/partials/sport-live-period.module.scss';

const propTypes = {
    className: PropTypes.string,
    sportGame: AppPropTypes.sportGame,
    liveTimer: PropTypes.bool,
    animationColor: PropTypes.string,
    inline: PropTypes.bool,
};

const defaultProps = {
    className: null,
    sportGame: null,
    liveTimer: false,
    animationColor: null,
    inline: false,
};

function SportLivePeriod({ className, sportGame, liveTimer, animationColor, inline }) {
    const themeClassName = useThemeClassName(styles);

    const {
        startAt,
        isStarted,
        isEnded,
        isUpcoming,
        periods,
        periodTime = null,
        periodRunning = false,
    } = sportGame || {};

    const currentPeriod = useMemo(() => {
        const sortedPeriods = [...(periods || [])];
        sortedPeriods.sort((a, b) => a.number - b.number);
        return sortedPeriods.reduce((selected, current) => {
            const { started = false, ended: periodEnded = false } = current;
            if (started || periodEnded) {
                return current;
            }
            return selected;
        }, null);
    }, [periods]);

    const {
        number: currentPeriodNumber = null,
        started: currentPeriodStarted = false,
        ended: currentPeriodEnded = false,
        overtime: currentPeriodOvertime = false,
    } = currentPeriod || {};

    const startAtDate = useMemo(() => parseISO(startAt), [startAt]);

    const hasPeriods = (periods || []).length > 0;

    const finalPeriodRunning = isStarted && currentPeriodStarted && periodRunning;

    const periodNumber = useMemo(() => {
        if (currentPeriodOvertime) {
            return <FormattedMessage defaultMessage="OT" description="Overtime abbreviate" />;
        }
        if (currentPeriodNumber !== null) {
            return <Ordinal number={currentPeriodNumber} feminine />;
        }

        return !isUpcoming ? (
            <FormattedMessage defaultMessage="Start on" description="Period start date" />
        ) : (
            <FormattedMessage defaultMessage="Start at" description="Period start time" />
        );
    }, [isStarted, isUpcoming, currentPeriod, currentPeriodOvertime, currentPeriodNumber]);

    const time = useMemo(() => {
        if (currentPeriodEnded) {
            return <FormattedMessage defaultMessage="Ended" description="Period status" />;
        }

        if (isStarted && currentPeriodStarted) {
            if (periodTime !== null) {
                return (
                    <Timer
                        separatorClassName={styles.timerSeparator}
                        delay={periodTime}
                        started={liveTimer && finalPeriodRunning}
                    />
                );
            }
        }

        if (!isUpcoming) {
            return <FormattedDate value={startAt} day="2-digit" month="short" />;
        }

        // if (!currentPeriodEnded && !currentPeriodStarted && currentPeriodNumber > 1) {
        //     return <FormattedMessage defaultMessage="Intermission" description="Period status" />;
        // }

        return <FormattedTime value={startAtDate} />;
    }, [
        currentPeriodEnded,
        currentPeriodStarted,
        periodTime,
        currentPeriodNumber,
        startAt,
        isUpcoming,
        isStarted,
    ]);

    return !hasPeriods ? null : (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [className]: className !== null,
                    [styles.periodRunning]: finalPeriodRunning,
                    [styles.inline]: inline,
                },
            ])}
        >
            {isEnded ? (
                <div className={styles.ended}>
                    <FormattedMessage defaultMessage="Final" description="PeriodStatus" />
                </div>
            ) : (
                <>
                    <div className={styles.number}>{periodNumber}</div>
                    <div className={styles.timeRemaining}>{time} </div>
                    <div className={styles.periodRunningLineContainer}>
                        <div
                            className={styles.periodRunningLine}
                            style={{ backgroundColor: animationColor }}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

SportLivePeriod.propTypes = propTypes;
SportLivePeriod.defaultProps = defaultProps;

export default SportLivePeriod;
